import getConfig from 'lib/next-config-compat';
import App, { type AppContext, type AppProps } from 'next/app';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { Cookies, CookiesProvider } from 'react-cookie';
import TagManager from 'react-gtm-module';
import type { BouncePage } from 'types/page';
import { refreshSession } from 'utils/refreshSession';
import MaintenancePage from '../components/maintenancePage';
import i18nConfig from '../lib/i18nConfig';
import AppProvider from '../state/app';
import AuthProvider from '../state/auth';
import BasketProvider from '../state/basket';
import BookingProvider from '../state/booking';
import BookingsProvider from '../state/bookings';
import FavouritesProvider from '../state/favourites';
import MobileHeaderProvider from '../state/mobileHeader';
import SearchProvider from '../state/search';
// utils
import { appWithTranslation } from '../utils/i18n';

// stylings
import '../styles/application.scss';
// The primary typeface. next/font emits its @font-face and --font-hind through
// a CSS module, and only the page entrypoints pull client CSS into the bundle —
// _document does not, so importing it there alone yields the class name with no
// font behind it. _document still puts hind.variable on <html>.
import 'lib/fonts';

const { publicRuntimeConfig } = getConfig();

const MyApp = ({
  Component,
  pageProps,
  cookies = {},
}: AppProps & { cookies?: Record<string, string> }) => {
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!publicRuntimeConfig.gtmId) {
      return undefined;
    }

    let initialised = false;
    const initGtm = () => {
      // Only load GTM once the visitor has consented to statistics cookies.
      // Cookiebot is in manual blocking mode, so it cannot gate the script
      // react-gtm-module injects at runtime — we have to check consent here.
      if (initialised || !window.Cookiebot?.consent?.statistics) {
        return;
      }
      initialised = true;
      TagManager.initialize({ gtmId: publicRuntimeConfig.gtmId });
    };

    // Returning visitors already have consent when this effect runs.
    initGtm();

    // CookiebotOnAccept fires once Cookiebot has determined consent (both on a
    // fresh accept and on load for visitors who consented previously).
    window.addEventListener('CookiebotOnAccept', initGtm);

    return () => window.removeEventListener('CookiebotOnAccept', initGtm);
  }, []);

  // Pages that only redirect opt out: see BouncePage for why starting a refresh
  // they cannot finish costs the user their session.
  const skipSessionRefresh =
    (Component as BouncePage).skipSessionRefresh === true;

  useEffect(() => {
    const refreshToken = async () => {
      try {
        if (cookies.refresh_token) {
          // Shared with AuthProvider's refreshAuthToken so the two cannot both
          // spend the same rotating cookie — see utils/refreshSession.
          setToken(await refreshSession());
        }
      } catch (_e) {
        // Only from the top window. This also runs inside the 3DS iframe, where
        // routing to /sign-in achieves nothing except a cross-origin fetch of
        // the page's data that the browser blocks.
        if (window.top === window.self) {
          Router.replace('/sign-in');
        }
      }
    };

    if (!skipSessionRefresh) {
      refreshToken();
    }
  }, [cookies.refresh_token, skipSessionRefresh]);

  const [cookieStore] = useState(() => new Cookies(cookies));

  if (publicRuntimeConfig.maintenance === 'true') {
    return <MaintenancePage />;
  }

  return (
    <CookiesProvider cookies={cookieStore}>
      <AppProvider>
        <AuthProvider token={token}>
          <MobileHeaderProvider>
            <SearchProvider>
              <FavouritesProvider>
                <BookingProvider>
                  <BasketProvider>
                    <BookingsProvider>
                      <Component {...pageProps} />
                    </BookingsProvider>
                  </BasketProvider>
                </BookingProvider>
              </FavouritesProvider>
            </SearchProvider>
          </MobileHeaderProvider>
        </AuthProvider>
      </AppProvider>
    </CookiesProvider>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { pageProps = {} } = await App.getInitialProps(appContext);
  const {
    ctx: { req: { cookies = {} } = {} },
  } = appContext as AppContext & {
    ctx: { req?: { cookies?: Record<string, string> } };
  };

  return { pageProps, cookies };
};

export default appWithTranslation(
  MyApp as Parameters<typeof appWithTranslation>[0],
  i18nConfig
);
